/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  PlusCircle,
  Building2,
  ChevronDown,
  Check,
  Palette,
  QrCode,
  ChevronRight
} from 'lucide-react';
import { subAccountService } from '../services/subAccountService';
import { accountService } from '../services/accountService';
import { useAuth } from '../context/AuthContext';
import toastService from '../utils/toastService';
import FileUpload from '../components/FileUpload';
import { DAISY_THEMES, FIELD_LIMITS } from '../utils/constants';
import { validateTextField } from '../utils/validation';

const ROUTE_SUB_ACCOUNTS = '/sub-accounts';
const CLASS_BORDER_ERROR = 'border-error ring-1 ring-error/20';
const CLASS_BORDER_DEFAULT = 'border-base-300';

const NewSubAccount = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [form, setForm] = useState({
    account_id: '',
    name: '',
    slug: '',
    status: 'active',
    theme: 'light',
    workflow_theme: 'light',
    logo_url: '',
    favicon_url: '',
    workflow_logo_url: '',
    show_qr_code: true
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountDropdownRef = React.useRef(null);
  const [subAccountBrandingOpen, setSubAccountBrandingOpen] = useState(false);
  const [workflowBrandingOpen, setWorkflowBrandingOpen] = useState(false);

  // Stable upload function — shared by brand logo and workflow logo fields
  const handleLogoUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file);
    return res.data; // { url: string }
  }, []);

  // Stable upload function for favicon
  const handleFaviconUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file, null, 'favicon');
    return res.data; // { url: string }
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (user?.scope === 'account') {
        // For account scope users, auto-set their accountUID
        setForm(f => ({ ...f, account_id: user.accountUID }));
        return;
      }

      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setParentAccounts(data.data || data || []);
      } catch (err) {
        toastService.error('Failed to load accounts');
      }
    };
    fetchAccounts();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!form.account_id) { errors.account_id = 'Parent account is required'; }
    else if (user?.scope === 'platform') {
      const selectedAccount = parentAccounts.find(acc => acc.id === form.account_id);
      if (selectedAccount?.status === 'inactive') {
        errors.account_id = 'Cannot create sub-account under an inactive account';
      }
    } else {
      // NO error
    }
    if (!form.name.trim()) { errors.name = 'Sub-account name is required'; }
    const nameError = validateTextField(form.name, 'Sub-account name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (nameError) { errors.name = nameError; }

    const slugError = validateTextField(form.slug, 'Your domain', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.SLUG_MAX_LENGTH });
    if (slugError) { errors.slug = slugError; }
    else if (!/^[a-z0-9-]+$/.test(form.slug.trim())) { errors.slug = 'Your domain must only contain lowercase letters, numbers, and hyphens'; }

    if (!form.workflow_theme) { errors.workflow_theme = 'Workflow theme is required'; }
    if (!form.workflow_logo_url) { errors.workflow_logo_url = 'Workflow logo is required'; }

    setFieldErrors(errors);

    // Auto-open workflow branding accordion if it has errors
    if (errors.workflow_theme || errors.workflow_logo_url) {
      setWorkflowBrandingOpen(true);
    }

    return Object.keys(errors).length === 0;
  };

  const handleFieldBlur = useCallback((fieldKey, value, fieldLabel, options) => {
    const errorMsg = validateTextField(value, fieldLabel, options);
    setFieldErrors(prev => ({ ...prev, [fieldKey]: errorMsg }));
  }, []);

  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setForm(f => ({ ...f, name, slug }));
    if (fieldErrors.name) {
      setFieldErrors(prev => ({ ...prev, name: validateTextField(name, 'Sub-account name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
    }
    if (fieldErrors.slug) setFieldErrors(prev => ({ ...prev, slug: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);

      const { data } = await subAccountService.createSubAccount(form.account_id, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        status: form.status,
        theme: form.theme,
        workflowTheme: form.workflow_theme || null,
        logoUrl: form.logo_url,
        faviconUrl: form.favicon_url,
        workflowLogoUrl: form.workflow_logo_url,
        showQrCode: form.show_qr_code
      });
      if (user?.subAccountUID && user.subAccountUID === data?.id) {
        updateProfile({
          subAccountLogoUrl: form.logo_url,
          subAccountTheme: form.theme,
          subAccountShowQrCode: form.show_qr_code
        });
      }
      toastService.success('Sub-account created successfully');
      navigate(ROUTE_SUB_ACCOUNTS);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to create sub-account');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">

        {/* Header Section */}
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title mb-1">Create Sub-Account</h1>
          </div>
          <button onClick={() => navigate(ROUTE_SUB_ACCOUNTS)} className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content">
            <X size={24} />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 sm:p-8 max-w-[1200px]">
          <form onSubmit={handleSubmit} className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Parent Account Selection - Only visible for platform scope users */}
              {user?.scope === 'platform' && (
                <div className="space-y-2">
                  <label htmlFor="parentAccount" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Parent Account *
                  </label>
                  <div className="relative" ref={accountDropdownRef}>
                    <button
                      id="parentAccount"
                      type="button"
                      onClick={() => setIsAccountOpen(!isAccountOpen)}
                      className={`flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 bg-base-100 border ${fieldErrors.account_id ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT} text-sm font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all group`}
                    >
                      <div className="flex items-center gap-2 truncate text-base-content">
                        <Building2 size={16} className="text-base-content/20 shrink-0" />
                        <span className="truncate">
                          {parentAccounts.find(a => a.id === form.account_id)?.name || 'Select Parent Account'}
                        </span>
                      </div>
                      <ChevronDown size={14} className={`text-base-content/20 group-hover:text-primary transition-transform shrink-0 ${isAccountOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isAccountOpen && (
                      <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-60 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            setForm(f => ({ ...f, account_id: '' }));
                            setIsAccountOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 ${!form.account_id ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${!form.account_id ? 'text-primary' : 'text-base-content/40 group-hover:text-primary'}`}>Select Parent Account</span>
                          {!form.account_id && <Check size={12} className="text-primary ml-auto" />}
                        </button>
                        {parentAccounts.map((acc) => (
                          <button
                            key={acc.id}
                            type="button"
                            disabled={acc.status === 'inactive'}
                            onClick={() => {
                              if (acc.status !== 'inactive') {
                                setForm(f => ({ ...f, account_id: acc.id }));
                                setIsAccountOpen(false);
                                if (fieldErrors.account_id) setFieldErrors(prev => ({ ...prev, account_id: null }));
                              }
                            }}
                            className={`w-full px-4 py-3 text-left transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${acc.status === 'inactive'
                              ? 'cursor-not-allowed opacity-50 bg-base-content/5'
                              : form.account_id === acc.id
                                ? 'bg-primary/5 hover:bg-primary/5'
                                : 'hover:bg-primary/5'
                              }`}
                          >
                            <div className="flex-1">
                              <span className={`text-[11px] font-black uppercase tracking-widest ${acc.status === 'inactive'
                                ? 'text-base-content/30'
                                : form.account_id === acc.id
                                  ? 'text-primary'
                                  : 'text-base-content/40 group-hover:text-primary'
                                }`}>
                                {acc.name}
                              </span>
                            </div>
                            {form.account_id === acc.id && acc.status !== 'inactive' && <Check size={12} className="text-primary ml-auto" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {fieldErrors.account_id && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.account_id}</p>}
                </div>
              )}

              {/* Sub-Account Name */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Sub-Account Name *
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder="e.g. Downtown Service Center"
                  className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.name ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                  value={form.name}
                  onChange={handleNameChange}
                  onBlur={() => handleFieldBlur('name', form.name, 'Sub-account name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
                />
                {fieldErrors.name && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.name}</p>}
              </div>

              {/* Status Dropdown */}
              {/*e.g. <div className="space-y-2">
                <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Status
                </label>
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                    className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 bg-base-100 border border-base-300 text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all group"
                  >
                    <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">{form.status}</span>
                    <ChevronDown size={14} className={`text-base-content/20 group-hover:text-primary transition-transform ${isStatusOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isStatusOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                      {['active', 'inactive'].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => {
                            setForm(f => ({ ...f, status: s }));
                            setIsStatusOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${form.status === s ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${form.status === s ? 'text-primary' : 'text-base-content/40 group-hover:text-primary'}`}>{s}</span>
                          {form.status === s && <Check size={12} className="text-primary ml-auto" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div> */}

            </div>

            {/* Subdomain */}
            <div className="grid grid-cols-2 gap-2 space-y-2">
              <div>
                <label htmlFor="slug" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Your domain *
                </label>
                <div className="relative">
                  <input
                    id="slug"
                    type="text"
                    placeholder="downtown-service-center"
                    className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.slug ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT} ${form.name ? '' : 'opacity-50'}`}
                    value={form.slug}
                    onChange={e => {
                      const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-');
                      setForm(f => ({ ...f, slug }));
                      if (fieldErrors.slug) {
                        setFieldErrors(prev => ({ ...prev, slug: validateTextField(slug, 'Your domain', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.SLUG_MAX_LENGTH }) }));
                      }
                    }}
                    onBlur={() => handleFieldBlur('slug', form.slug, 'Your domain', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.SLUG_MAX_LENGTH })}
                  />
                </div>
              </div>
              <span className="font-bold text-[15px] flex items-center pt-2">.{process.env.REACT_APP_BASE_URL_HOSTNAME}</span>
              {fieldErrors.slug && <p className="text-error text-xs font-medium !mt-0">{fieldErrors.slug}</p>}
            </div>

            {/* Sub-Account Branding Section */}
            <div className="border border-base-300 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setSubAccountBrandingOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-4 bg-base-100 hover:bg-base-200 transition"
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Palette size={14} className="text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-base-content">Sub-Account Branding</p>
                    <p className="text-xs text-base-content/50">Theme, logo and favicon for this sub-account</p>
                  </div>
                </div>
                <ChevronRight size={16} className={`text-base-content/30 transition-transform ${subAccountBrandingOpen ? 'rotate-90' : ''}`} />
              </button>

              {subAccountBrandingOpen && (
                <div className="px-5 pb-5 pt-5 border-t border-base-300 space-y-5 bg-base-200/50">
                  {/* Experience Theme */}
                  <div>
                    <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Experience Theme</label>
                    <div className="flex items-center gap-2 flex-wrap">
                      {DAISY_THEMES.map((theme) => {
                        const isActive = form.theme === theme;
                        return (
                          <label key={theme} className={`relative flex items-center gap-2 p-1.5 rounded-xl transition-all duration-200 select-none border-2 cursor-pointer ${isActive ? 'border-primary bg-primary/10 shadow-sm' : 'border-base-300 bg-base-100 hover:border-primary/30 hover:bg-base-200'}`}>
                            <input type="radio" name="sub-account-theme" value={theme} checked={isActive} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="sr-only" />
                            <div data-theme={theme} className="bg-base-100 grid grid-cols-2 gap-0.5 rounded-lg p-1 shadow-sm border border-base-content/5 w-7 h-7 shrink-0 place-items-center relative">
                              <div className="bg-base-content w-1 h-1 rounded-full" />
                              <div className="bg-primary w-1 h-1 rounded-full" />
                              <div className="bg-secondary w-1 h-1 rounded-full" />
                              <div className="bg-accent w-1 h-1 rounded-full" />
                              {isActive && (<div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center"><svg className="w-3.5 h-3.5 text-primary drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div>)}
                            </div>
                            <span className={`text-[11px] font-bold capitalize truncate leading-none pr-1 ${isActive ? 'text-primary' : 'text-base-content/70'}`}>{theme}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Brand Logo */}
                  <div className="mt-4">
                    <FileUpload
                      id="logo_url"
                      label="Brand Logo"
                      currentUrl={form.logo_url}
                      onUploadComplete={(url) => setForm(f => ({ ...f, logo_url: url }))}
                      onClear={() => setForm(f => ({ ...f, logo_url: '' }))}
                      uploadFn={handleLogoUpload}
                      maxSize={10 * 1024 * 1024}
                    />
                  </div>

                  {/* Favicon */}
                  <div>
                    <FileUpload
                      id="favicon_url"
                      label="Favicon"
                      currentUrl={form.favicon_url}
                      onUploadComplete={(url) => setForm(f => ({ ...f, favicon_url: url }))}
                      onClear={() => setForm(f => ({ ...f, favicon_url: '' }))}
                      uploadFn={handleFaviconUpload}
                      maxSize={5 * 1024 * 1024}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Workflow Branding Section */}
            <div className="border border-base-300 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setWorkflowBrandingOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-4 bg-base-100 hover:bg-base-200 transition"
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Palette size={14} className="text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-base-content">Workflow Branding</p>
                    <p className="text-xs text-base-content/50">Default theme and logo for workflows under this sub-account</p>
                  </div>
                </div>
                <ChevronRight size={16} className={`text-base-content/30 transition-transform ${workflowBrandingOpen ? 'rotate-90' : ''}`} />
              </button>

              {workflowBrandingOpen && (
                <div className="px-5 pb-5 pt-5 border-t border-base-300 space-y-5 bg-base-200/50">
                  {/* Workflow Theme */}
                  <div>
                    <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Workflow Theme *</label>
                    <p className="text-base-content/40 text-[10px] font-medium mb-2">Default theme applied when creating workflows under this sub-account.</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {DAISY_THEMES.map((theme) => {
                        const isActive = form.workflow_theme === theme;
                        return (
                          <label key={theme} className={`relative flex items-center gap-2 p-1.5 rounded-xl transition-all duration-200 select-none border-2 cursor-pointer ${isActive ? 'border-primary bg-primary/10 shadow-sm' : 'border-base-300 bg-base-100 hover:border-primary/30 hover:bg-base-200'}`}>
                            <input type="radio" name="sub-account-workflow-theme" value={theme} checked={isActive} onChange={(e) => { setForm({ ...form, workflow_theme: e.target.value }); setFieldErrors(prev => ({ ...prev, workflow_theme: '' })); }} className="sr-only" />
                            <div data-theme={theme} className="bg-base-100 grid grid-cols-2 gap-0.5 rounded-lg p-1 shadow-sm border border-base-content/5 w-7 h-7 shrink-0 place-items-center relative">
                              <div className="bg-base-content w-1 h-1 rounded-full" />
                              <div className="bg-primary w-1 h-1 rounded-full" />
                              <div className="bg-secondary w-1 h-1 rounded-full" />
                              <div className="bg-accent w-1 h-1 rounded-full" />
                              {isActive && (<div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center"><svg className="w-3.5 h-3.5 text-primary drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></div>)}
                            </div>
                            <span className={`text-[11px] font-bold capitalize truncate leading-none pr-1 ${isActive ? 'text-primary' : 'text-base-content/70'}`}>{theme}</span>
                          </label>
                        );
                      })}
                    </div>
                    {fieldErrors.workflow_theme && <p className="text-error text-xs mt-2 font-medium">{fieldErrors.workflow_theme}</p>}
                  </div>

                  {/* Workflow Logo */}
                  <div>
                    <FileUpload
                      id="workflow_logo_url"
                      label="Workflow Logo *"
                      description="Default logo shown on workflow capture screens for this sub-account."
                      currentUrl={form.workflow_logo_url}
                      onUploadComplete={(url) => { setForm(f => ({ ...f, workflow_logo_url: url })); setFieldErrors(prev => ({ ...prev, workflow_logo_url: '' })); }}
                      onClear={() => setForm(f => ({ ...f, workflow_logo_url: '' }))}
                      uploadFn={handleLogoUpload}
                      maxSize={10 * 1024 * 1024}
                    />
                    {fieldErrors.workflow_logo_url && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.workflow_logo_url}</p>}
                  </div>
                </div>
              )}
            </div>
            {/* Show QR Code Toggle */}
            <div className="flex items-center justify-between p-4 bg-base-100 border border-base-300 rounded-xl">
                <div>
                  <span className="block text-sm font-bold text-base-content">Enable QR Code Access</span>
                  <span className="block text-xs text-base-content/50 mt-0.5">Show QR codes for workflow assignments created under this sub-account.</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={form.show_qr_code}
                    onChange={(e) => setForm({ ...form, show_qr_code: e.target.checked })}
                  />
                  <div className="w-11 h-6 bg-base-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

            {/* Actions */}
            <div className="flex gap-4 pt-12 border-t border-base-content/5">
              <button
                type="button"
                onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
                className="flex-1 py-4 px-8 border border-base-content/20 rounded-xl font-black uppercase tracking-[0.2em] text-xs text-base-content/40 hover:bg-base-content/5 hover:text-base-content transition-all"
              >
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="flex-1 btn-premium">
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <PlusCircle size={18} />
                    <span>Create Sub-Account</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewSubAccount;
